import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageCircle, MessageSquare, Check, Trash2, Loader2, Wifi, WifiOff, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL?.replace(/^http/, 'ws');

export const NotificationDropdown = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // 벨(종)용 — 운영팀(admin) 미읽음 알림 수만 조회
  const fetchAdminUnread = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/notifications/unread-count?category=admin`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (error) { /* silent */ }
  }, [token]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!token || !isAuthenticated) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      const ws = new WebSocket(`${WS_URL}/api/ws/notifications?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connected");
        setIsConnected(true);
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send("ping");
          }
        }, 25000); // Ping every 25 seconds
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "initial") {
            // 벨에는 운영팀(admin) 알림만 → admin 미읽음 수를 별도 조회
            fetchAdminUnread();
          } else if (data.type === "new_notification") {
            // 운영팀 알림만 벨에 반영
            if (data.notification?.type === "admin_message") {
              setUnreadCount(prev => prev + 1);
              if (isOpen) {
                setNotifications(prev => [data.notification, ...prev]);
              }
              if (Notification.permission === "granted") {
                new Notification("New Coupling", {
                  body: getNotificationMessage(data.notification),
                  icon: "/favicon.ico"
                });
              }
            }
          }
        } catch (e) {
          // Ignore non-JSON messages (like "pong")
        }
      };

      ws.onclose = (event) => {
        console.log("WebSocket disconnected:", event.code);
        setIsConnected(false);
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Reconnect after 5 seconds if not intentionally closed
        if (event.code !== 1000 && isAuthenticated) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
    } catch (error) {
      console.error("Failed to create WebSocket:", error);
      // Fallback to polling if WebSocket fails
      setIsConnected(false);
    }
  }, [token, isAuthenticated, isOpen]);

  // Connect WebSocket on mount
  useEffect(() => {
    if (isAuthenticated && token) {
      // Request notification permission
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
      
      connectWebSocket();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounting");
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [isAuthenticated, token, connectWebSocket]);

  // Fallback polling when WebSocket is not connected (admin 알림 수)
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    fetchAdminUnread(); // 마운트 시 1회
    if (isConnected) return;
    const interval = setInterval(fetchAdminUnread, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, token, isConnected, fetchAdminUnread]);

  const getNotificationMessage = (notification) => {
    switch (notification.type) {
      case "comment":
        return `${notification.actor_name}님이 회원님의 게시글에 댓글을 남겼습니다.`;
      case "reply":
        return `${notification.actor_name}님이 회원님의 댓글에 답글을 남겼습니다.`;
      case "admin_message":
        return `🛡 운영팀(${notification.actor_name})에서 메시지를 보냈습니다.`;
      default:
        return "새로운 알림이 있습니다.";
    }
  };

  const fetchNotifications = async () => {
    if (!token) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications?limit=10&category=admin`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      try {
        await fetch(`${API_URL}/api/notifications/${notification.id}/read`, {
          method: "PUT",
          headers: { "Authorization": `Bearer ${token}` },
        });
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Failed to mark as read:", error);
      }
    }

    // Navigate based on notification type
    setIsOpen(false);
    if (notification.type === "admin_message") {
      navigate("/messages");
    } else if (notification.post_id) {
      navigate(`/community/post/${notification.post_id}`);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/read-all?category=admin`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${token}` },
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` },
      });
      const deleted = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (deleted && !deleted.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return "방금 전";
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    if (diffDay < 7) return `${diffDay}일 전`;
    return date.toLocaleDateString("ko-KR");
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "comment":
        return <MessageCircle className="w-4 h-4 text-burgundy-500" />;
      case "reply":
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case "admin_message":
        return <Mail className="w-4 h-4 text-burgundy-600" />;
      default:
        return <Bell className="w-4 h-4 text-stone-500" />;
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-full hover:bg-stone-100 transition-colors"
        data-testid="notification-bell"
        title={isConnected ? "실시간 알림 연결됨" : "알림"}
      >
        <Bell className="w-5 h-5 text-stone-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
        {/* WebSocket connection indicator */}
        {isConnected && (
          <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full" title="실시간 연결됨" />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-stone-200 overflow-hidden z-50">
          {/* Header */}
          <div className="p-4 border-b border-stone-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-stone-800">운영팀 알림</h3>
              {isConnected ? (
                <span className="flex items-center gap-1 text-xs text-green-600" title="실시간 알림 활성화">
                  <Wifi className="w-3 h-3" />
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-stone-400" title="폴링 모드">
                  <WifiOff className="w-3 h-3" />
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-sm text-burgundy-500 hover:text-burgundy-600 flex items-center gap-1"
                data-testid="mark-all-read"
              >
                <Check className="w-4 h-4" />
                모두 읽음
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-burgundy-500 mx-auto" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-stone-500">
                <Bell className="w-10 h-10 mx-auto mb-2 text-stone-300" />
                <p>운영팀 알림이 없습니다</p>
                <p className="text-xs text-stone-400 mt-1">리뉴얼·프로필 업데이트 등 안내가 여기에 표시됩니다</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 border-b border-stone-50 cursor-pointer hover:bg-stone-50 transition-colors ${
                    !notification.is_read ? "bg-burgundy-50/50" : ""
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-stone-800 leading-tight">
                        {getNotificationMessage(notification)}
                      </p>
                      <p className="text-xs text-stone-500 mt-1 truncate">
                        "{notification.content_preview}"
                      </p>
                      <p className="text-xs text-stone-400 mt-1">
                        {getRelativeTime(notification.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => handleDeleteNotification(e, notification.id)}
                      className="flex-shrink-0 p-1 text-stone-300 hover:text-red-500 transition-colors"
                      data-testid={`delete-notification-${notification.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer - View All */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-stone-100 text-center">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate("/messages");
                }}
                className="text-sm text-burgundy-500 hover:text-burgundy-600 font-medium"
              >
                모든 운영팀 알림 보기
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
